/**
 * Purpose: Top-down classroom grid view — the visual heart of the School Day board.
 * Owns: Chalkboard header, teacher's desk, desk grid orchestration, seat advancement
 *       animation, class graduation celebration, hall pass transition, compact scoreboard.
 * Notes: Replaces the old BoardPath winding-path view with a per-class desk grid.
 *        Reads all state from GameContext. Animation state is managed locally with
 *        useRef/useState to track seat transitions. Grid dimensions come from the
 *        player's current ClassPeriod (via classConfig, CS-21).
 *        All colors use theme tokens (CS-22). No magic numbers (CS-20).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useGame } from '../../context/GameContext';
import { DeskGrid } from './DeskGrid';
import type { Player, ClassPeriod } from '../../types/game';
import type { Category } from '../../types/question';

// _______________________________________________
//              SUBJECT DISPLAY CONFIG
// _______________________________________________
// Icons and labels for each subject shown on the chalkboard header (CS-21).

const SUBJECT_ICONS: Record<Category | 'Final Exam', string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
  'Final Exam': '\u{1F393}',
};

// _______________________________________________
//              ANIMATION TIMING
// _______________________________________________

/** Milliseconds per seat during advancement animation */
const MS_PER_SEAT = 300;

/** Duration for the "CLASS PASSED!" banner in ms */
const GRADUATION_BANNER_DURATION = 2000;

/** Duration for the hall pass interstitial in ms */
const HALL_PASS_DURATION = 1800;

// _______________________________________________
//              OVERLAY STATE TYPES
// _______________________________________________

type OverlayState =
  | { kind: 'none' }
  | { kind: 'graduation'; subject: string; grade: string }
  | { kind: 'hallPass'; nextPeriod: number; nextSubject: string };

// _______________________________________________
//              MAIN COMPONENT
// _______________________________________________

interface ClassroomViewProps {
  /** Callback when the user taps "Next Turn" / "Continue" */
  onNextTurn: () => void;
  /** Whether the last answer was correct (null if N/A) */
  wasCorrect: boolean | null;
  /** Points earned on this turn */
  pointsEarned: number;
}

export function ClassroomView({ onNextTurn, wasCorrect, pointsEarned }: ClassroomViewProps) {
  const { state } = useGame();
  const activePlayer = state.players[state.currentPlayerIndex];

  /* Track the previous seat/period to detect movement and trigger animations */
  const prevSeatRef = useRef<number | null>(null);
  const prevPeriodRef = useRef<number | null>(null);

  /* Seat currently being highlighted during animation */
  const [animatingSeat, setAnimatingSeat] = useState<number | null>(null);

  /* Overlay for graduation celebration or hall pass interstitial */
  const [overlay, setOverlay] = useState<OverlayState>({ kind: 'none' });

  /* Which period's classroom to display (may differ from player's currentPeriod during transitions) */
  const [displayPeriod, setDisplayPeriod] = useState<number>(activePlayer?.currentPeriod ?? 1);

  /**
   * Get the ClassPeriod for a given period number (1-indexed).
   * Falls back to the player's current period if out of range.
   */
  const getPeriodData = useCallback(
    (periodNumber: number): ClassPeriod | null => {
      if (!activePlayer) return null;
      const index = periodNumber - 1;
      return activePlayer.schedule[index] ?? null;
    },
    [activePlayer],
  );

  /* Detect seat/period changes and trigger animations */
  useEffect(() => {
    if (!activePlayer) return;

    const currentPeriodIndex = activePlayer.currentPeriod - 1;
    const currentProgress = activePlayer.classProgress[currentPeriodIndex];
    const currentSeat = currentProgress?.currentSeat ?? 0;
    const currentPeriod = activePlayer.currentPeriod;

    const prevSeat = prevSeatRef.current;
    const prevPeriod = prevPeriodRef.current;

    /* First render — just store the values, no animation */
    if (prevSeat === null || prevPeriod === null) {
      prevSeatRef.current = currentSeat;
      prevPeriodRef.current = currentPeriod;
      setDisplayPeriod(currentPeriod);
      return;
    }

    /* Player answered wrong or no movement — no animation needed */
    if (prevPeriod === currentPeriod && prevSeat === currentSeat) {
      prevSeatRef.current = currentSeat;
      prevPeriodRef.current = currentPeriod;
      return;
    }

    const didGraduate = prevPeriod < currentPeriod;

    /* If player advanced seats within the same period */
    if (!didGraduate && prevSeat < currentSeat) {
      runSeatAnimation(prevSeat, currentSeat);
    }

    /* If player graduated to a new period */
    if (didGraduate) {
      const oldPeriodData = getPeriodData(prevPeriod);
      const oldProgress = activePlayer.classProgress[prevPeriod - 1];
      const lastSeat = oldPeriodData?.classSize.seats ?? currentSeat;

      /* Animate to last seat of old class, then show graduation */
      setDisplayPeriod(prevPeriod);
      runSeatAnimation(prevSeat, lastSeat, () => {
        /* Show graduation banner */
        const grade = oldProgress?.letterGrade ?? 'A';
        const subject = oldPeriodData?.subject ?? 'Class';
        setOverlay({ kind: 'graduation', subject, grade });

        setTimeout(() => {
          /* Show hall pass interstitial */
          const nextPeriodData = getPeriodData(currentPeriod);
          const nextSubject = nextPeriodData?.subject ?? 'Class';
          setOverlay({ kind: 'hallPass', nextPeriod: currentPeriod, nextSubject });

          setTimeout(() => {
            /* Transition to new classroom */
            setOverlay({ kind: 'none' });
            setDisplayPeriod(currentPeriod);
          }, HALL_PASS_DURATION);
        }, GRADUATION_BANNER_DURATION);
      });
    }

    prevSeatRef.current = currentSeat;
    prevPeriodRef.current = currentPeriod;
  }, [activePlayer, getPeriodData]);

  /**
   * Animate a player token sliding through seats from → to.
   * Highlights each intermediate seat for MS_PER_SEAT, then calls onComplete.
   */
  function runSeatAnimation(from: number, to: number, onComplete?: () => void) {
    const seats: number[] = [];
    for (let s = from + 1; s <= to; s++) {
      seats.push(s);
    }

    if (seats.length === 0) {
      onComplete?.();
      return;
    }

    let i = 0;
    function step() {
      if (i < seats.length) {
        setAnimatingSeat(seats[i]);
        i++;
        setTimeout(step, MS_PER_SEAT);
      } else {
        setAnimatingSeat(null);
        onComplete?.();
      }
    }
    step();
  }

  /* Bail if no active player (shouldn't happen in normal flow) */
  if (!activePlayer) return null;

  const periodData = getPeriodData(displayPeriod);
  if (!periodData) return null;

  const subjectName = periodData.subject;
  const subjectIcon = SUBJECT_ICONS[subjectName as keyof typeof SUBJECT_ICONS] ?? '';

  return (
    <div className="chalkboard-bg" style={viewStyles.page}>
      {/* Result banner — same pattern as existing BoardPage */}
      {wasCorrect !== null && (
        <ResultBanner wasCorrect={wasCorrect} pointsEarned={pointsEarned} />
      )}

      {/* Overlay screens (graduation celebration or hall pass) */}
      {overlay.kind !== 'none' && (
        <OverlayScreen overlay={overlay} />
      )}

      {/* Chalkboard header — period number and subject */}
      <ChalkboardHeader
        periodNumber={displayPeriod}
        subjectName={subjectName}
        subjectIcon={subjectIcon}
      />

      {/* Teacher's desk */}
      <TeacherDesk />

      {/* Desk grid — the main classroom view */}
      <div style={viewStyles.gridArea}>
        <DeskGrid
          classSize={periodData.classSize}
          players={state.players}
          periodIndex={displayPeriod - 1}
          activePlayerId={activePlayer.id}
          animatingSeat={animatingSeat}
        />
      </div>

      {/* Bottom section: compact scoreboard + action button */}
      <div style={viewStyles.bottomSection}>
        <button
          className="btn-gold"
          style={viewStyles.actionButton}
          onClick={onNextTurn}
          aria-label="Next turn"
        >
          Next Turn
        </button>

        <ClassroomScoreboard
          players={state.players}
          activePlayerId={activePlayer.id}
        />
      </div>
    </div>
  );
}

// _______________________________________________
//              CHALKBOARD HEADER
// _______________________________________________
// Displays "Period [X]: [Subject]" with emoji on a chalkboard strip.

function ChalkboardHeader({
  periodNumber,
  subjectName,
  subjectIcon,
}: {
  periodNumber: number;
  subjectName: string;
  subjectIcon: string;
}) {
  return (
    <div style={viewStyles.chalkboardHeader}>
      <span className="chalk-text-bright" style={viewStyles.periodText}>
        {subjectIcon} Period {periodNumber}: {subjectName}
      </span>
    </div>
  );
}

// _______________________________________________
//              TEACHER'S DESK
// _______________________________________________
// Simple rectangular element below the chalkboard header.

function TeacherDesk() {
  return (
    <div style={viewStyles.teacherDeskContainer}>
      <div style={viewStyles.teacherDesk}>
        <span className="chalk-text" style={viewStyles.teacherLabel}>
          Teacher
        </span>
      </div>
    </div>
  );
}

// _______________________________________________
//              RESULT BANNER
// _______________________________________________
// Shows "CORRECT!" or "WRONG!" with points earned.

function ResultBanner({
  wasCorrect,
  pointsEarned,
}: {
  wasCorrect: boolean;
  pointsEarned: number;
}) {
  return (
    <div
      className="banner-pop"
      style={{
        ...viewStyles.banner,
        backgroundColor: wasCorrect
          ? 'rgba(74, 103, 65, 0.85)'
          : 'rgba(139, 64, 73, 0.85)',
      }}
      role="alert"
    >
      <span className="chalk-text-bright" style={viewStyles.bannerHeading}>
        {wasCorrect ? 'CORRECT!' : 'WRONG!'}
      </span>
      {wasCorrect && pointsEarned > 0 && (
        <span className="chalk-text-gold" style={viewStyles.bannerPoints}>
          +{pointsEarned} points!
        </span>
      )}
    </div>
  );
}

// _______________________________________________
//              OVERLAY SCREENS
// _______________________________________________
// Full-screen overlays for class graduation and hall pass transitions.

function OverlayScreen({ overlay }: { overlay: OverlayState }) {
  if (overlay.kind === 'graduation') {
    return (
      <div className="banner-pop" style={viewStyles.overlay} role="alert">
        <span className="chalk-text-bright" style={viewStyles.overlayHeading}>
          CLASS PASSED!
        </span>
        <span className="chalk-text-gold" style={viewStyles.overlayGrade}>
          {overlay.subject}: {overlay.grade}!
        </span>
      </div>
    );
  }

  if (overlay.kind === 'hallPass') {
    const icon = SUBJECT_ICONS[overlay.nextSubject as keyof typeof SUBJECT_ICONS] ?? '';
    return (
      <div className="banner-pop" style={viewStyles.overlay} role="status">
        <span className="chalk-text" style={viewStyles.overlayHallPass}>
          {'\u{1F3AB}'} Hall Pass
        </span>
        <span className="chalk-text-bright" style={viewStyles.overlaySubject}>
          Walking to Period {overlay.nextPeriod}: {icon} {overlay.nextSubject}...
        </span>
      </div>
    );
  }

  return null;
}

// _______________________________________________
//              CLASSROOM SCOREBOARD
// _______________________________________________
// Compact bottom bar showing each player's current period and GPA.

function ClassroomScoreboard({
  players,
  activePlayerId,
}: {
  players: Player[];
  activePlayerId: string;
}) {
  return (
    <div style={viewStyles.scoreboardBar} role="region" aria-label="Player progress">
      {players.map((player) => {
        const isActive = player.id === activePlayerId;
        const periodLabel = player.finished
          ? 'Done'
          : `P${player.currentPeriod}`;

        return (
          <div
            key={player.id}
            style={{
              ...viewStyles.scoreChip,
              ...(isActive ? viewStyles.scoreChipActive : {}),
            }}
            aria-label={`${player.name}: Period ${player.currentPeriod}, GPA ${player.gpa.toFixed(1)}`}
          >
            {/* Avatar circle */}
            <div
              style={{
                ...viewStyles.avatar,
                backgroundColor: player.color,
                ...(isActive ? viewStyles.avatarActive : {}),
              }}
            >
              {player.name.charAt(0).toUpperCase()}
            </div>

            <div style={viewStyles.scoreInfo}>
              <span style={viewStyles.scoreName}>{player.name}</span>
              <span style={viewStyles.scorePeriod}>{periodLabel}</span>
              <span style={viewStyles.scoreGpa}>
                GPA {player.gpa.toFixed(1)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// _______________________________________________
//              STYLES
// _______________________________________________

const viewStyles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    position: 'relative',
  },

  /* Result banner at the top */
  banner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.6rem 1rem',
    textAlign: 'center' as const,
  },
  bannerHeading: {
    fontSize: '1.6rem',
    lineHeight: 1.1,
  },
  bannerPoints: {
    fontSize: '1rem',
    fontFamily: 'var(--font-family-handwriting)',
  },

  /* Chalkboard header strip */
  chalkboardHeader: {
    padding: '0.6rem 1rem',
    textAlign: 'center' as const,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderBottom: '3px solid var(--accent-wood)',
  },
  periodText: {
    fontSize: '1.2rem',
    letterSpacing: '0.04em',
  },

  /* Teacher's desk */
  teacherDeskContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0.5rem 1rem 0.25rem',
  },
  teacherDesk: {
    width: '120px',
    height: '32px',
    backgroundColor: 'var(--accent-wood)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  teacherLabel: {
    fontSize: '0.65rem',
    opacity: 0.7,
    letterSpacing: '0.08em',
  },

  /* Scrollable grid area */
  gridArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: '0.5rem',
  },

  /* Bottom section */
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flexShrink: 0,
  },
  actionButton: {
    margin: '0.5rem 1rem',
    padding: '0.75rem',
    fontSize: '1.1rem',
    fontFamily: 'var(--font-family)',
  },

  /* Overlay (graduation / hall pass) */
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    backgroundColor: 'rgba(30, 58, 47, 0.92)',
  },
  overlayHeading: {
    fontSize: '2.2rem',
    lineHeight: 1.1,
  },
  overlayGrade: {
    fontSize: '1.6rem',
  },
  overlayHallPass: {
    fontSize: '1.3rem',
    opacity: 0.7,
  },
  overlaySubject: {
    fontSize: '1.2rem',
    textAlign: 'center' as const,
    padding: '0 1rem',
  },

  /* Classroom scoreboard */
  scoreboardBar: {
    display: 'flex',
    gap: '0.4rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '12px 12px 0 0',
    overflowX: 'auto',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  scoreChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.3rem 0.5rem',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    minWidth: '72px',
  },
  scoreChipActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    boxShadow: '0 0 0 2px var(--accent-gold)',
  },
  avatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-light)',
    flexShrink: 0,
  },
  avatarActive: {
    boxShadow: '0 0 6px var(--accent-gold)',
  },
  scoreInfo: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.1,
  },
  scoreName: {
    fontSize: '0.65rem',
    color: 'var(--text-light)',
    opacity: 0.85,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '55px',
  },
  scorePeriod: {
    fontSize: '0.6rem',
    color: 'var(--text-light)',
    opacity: 0.6,
    fontFamily: 'var(--font-family)',
  },
  scoreGpa: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family)',
  },
};
